import { useState, useEffect } from 'react';
import {
  Outlet,
  Link,
  useLocation,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { useAtom } from 'jotai';
import { authStateAtom } from '@/lib/auth';
import { Globe2, Loader2, Settings as SettingsIcon } from 'lucide-react';
import { AdminNavRail } from './AdminNavRail';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [authState] = useAtom(authStateAtom);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authState.isLoading) {
      setIsLoading(false);
    }
  }, [authState.isLoading]);

  const handleNavRailSettingsClick = () => {
    navigate('/admin/settings?settings_section=platform');
  };

  const isAdmin = authState?.user?.role === 'admin';
  const isAuthLoading = authState.isLoading;

  // Check if user is admin - also check app_metadata for platform admin role
  const isAppAdmin = authState?.user?.app_metadata?.role === 'admin';
  const hasAdminAccess = isAdmin || isAppAdmin;

  // Check if user is admin
  if (!isAuthLoading && (!authState.user || !hasAdminAccess)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 h-16">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/admin" className="flex items-center space-x-3 ml-2">
              <img
                src="/images/terrarium-logo.png"
                alt="Terrarium Logo"
                className="h-8 w-8 object-contain"
              />
              <span className="font-perfectly-nineties text-2xl font-semibold text-primary">
                terrarium
              </span>
            </Link>
            <>
              <span className="ml-4 text-slate-300">|</span>
              <span className="ml-4 text-sm font-medium text-slate-600">
                Platform Admin Area
              </span>
            </>
          </div>
        </div>
      </nav>

      <div className="pt-16 flex h-[calc(100vh-4rem)]">
        <AdminNavRail onSettingsClick={handleNavRailSettingsClick} />
        <main className="flex-1 overflow-y-auto transition-all duration-300 md:ml-20">
          {isLoading && !children && !(<Outlet />) ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
